/**
 * Beautiful HTML Template for Horoscope Casting & Matching PDF
 * Generates professional, print-ready output with:
 *  - Sri Paranthaman letterhead with logo
 *  - South Indian Rasi/Navamsa charts as styled HTML tables
 *  - Bilingual Tamil/English support
 *  - Color-coded sections, elegant typography
 */

import { HoroscopeLang, getLangDict } from './horoscope-lang';

// ─── SOUTH INDIAN CHART (HTML TABLE) ─────────────────────────────────────────
// Maps rasi index (0=Mesha) to South Indian grid position [row, col]
const SI_GRID: Record<number, [number, number]> = {
  11: [0, 0], 0: [0, 1], 1: [0, 2], 2: [0, 3],
  10: [1, 0],                         3: [1, 3],
   9: [2, 0],                         4: [2, 3],
   8: [3, 0], 7: [3, 1], 6: [3, 2], 5: [3, 3],
};

function renderChartHTML(planets: Record<number, string[]>, title: string, _lang?: HoroscopeLang): string {
  const grid: string[][] = Array.from({ length: 4 }, () => Array(4).fill(''));

  for (let rasi = 0; rasi < 12; rasi++) {
    const pos = SI_GRID[rasi];
    if (pos) {
      const pList = (planets[rasi] || []).join(' ');
      grid[pos[0]][pos[1]] = pList;
    }
  }

  let html = `<table class="si-chart"><caption>${title}</caption>`;
  for (let r = 0; r < 4; r++) {
    html += '<tr>';
    for (let c = 0; c < 4; c++) {
      if ((r === 1 || r === 2) && (c === 1 || c === 2)) {
        if (r === 1 && c === 1) {
          html += `<td colspan="2" rowspan="2" class="chart-center">${title}</td>`;
        } else if (r === 1 && c === 2) continue;
        else if (r === 2 && (c === 1 || c === 2)) continue;
      } else {
        html += `<td class="chart-cell">${grid[r][c]}</td>`;
      }
    }
    html += '</tr>';
  }
  html += '</table>';
  return html;
}

// ─── CSS STYLES ──────────────────────────────────────────────────────────────
const PDF_CSS = `
@page { size: A4; margin: 0; }
* { box-sizing: border-box; margin: 0; padding: 0; }
body { 
  font-family: 'Noto Sans Tamil', 'Noto Sans', 'Latha', 'Bamini', Arial, sans-serif; 
  background: white; 
}

/* ── STRICT PAGE LAYOUT ── */
.page {
  width: 210mm; height: 296mm;
  padding: 10mm 15mm;
  margin: 0 auto;
  page-break-after: always;
  display: flex; flex-direction: column;
  overflow: hidden;
}
.page-content {
  flex-grow: 1; display: flex; flex-direction: column;
  justify-content: flex-start; padding-bottom: 5px;
}

/* ── LETTERHEAD ── */
.letterhead {
  display: flex; align-items: center; 
  border-bottom: 3px solid #8B0000; padding-bottom: 8px; margin-bottom: 10px;
}
.letterhead img { width: 70px; height: 70px; object-fit: contain; margin-right: 15px; }
.letterhead-text { flex: 1; text-align: center; }
.letterhead-text h1 { 
  font-size: 16pt; color: #8B0000; font-weight: bold; 
  margin: 0; letter-spacing: 1px;
}
.letterhead-text h2 { font-size: 11pt; color: #333; margin: 2px 0; }
.letterhead-text p { font-size: 8pt; color: #666; }
.letterhead-right { width: 70px; text-align: right; font-size: 8pt; color: #999; }

/* ── BIRTH INFO ── */
.birth-info { 
  background: #FFF8F0; border: 1px solid #D4A574; border-radius: 6px;
  padding: 10px 14px; margin: 8px 0;
}
.birth-info table { width: 100%; border-collapse: collapse; }
.birth-info td { padding: 2px 6px; font-size: 9.5pt; }
.birth-info .label { font-weight: bold; color: #8B0000; width: 30%; }
.birth-info .value { color: #333; }

/* ── PANCHANGA LINE ── */
.panchanga-line {
  background: linear-gradient(135deg, #1b5e20, #0d47a1);
  color: #ffffff;
  font-weight: bold;
  padding: 8px 14px;
  border-radius: 6px;
  margin: 8px 0;
  font-size: 10pt;
  text-align: center;
  letter-spacing: 0.3px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.12);
  border: 1px solid #0d47a1;
}
.tamil-date-badge {
  color: #FFD700;
  font-weight: 900;
  text-shadow: 0 1px 2px rgba(0,0,0,0.4);
}

/* ── SECTION HEADERS ── */
.section-title {
  background: linear-gradient(90deg, #8B0000, #A0522D);
  color: white; font-weight: bold; font-size: 10pt;
  padding: 4px 10px; border-radius: 3px; margin: 8px 0 4px;
  text-align: left;
}

/* ── PLANET TABLE ── */
.planet-table { width: 100%; border-collapse: collapse; margin-bottom: 10px; page-break-inside: avoid; }
.planet-table th { 
  background: #8B0000; color: white; font-size: 10pt; 
  padding: 4px; text-align: center;
}
.planet-table td { 
  padding: 3px 5px; font-size: 9pt; border-bottom: 1px solid #E8D5C4;
  text-align: center;
}
.planet-table tr:nth-child(even) { background: #FFF8F0; }
.planet-table .planet-name { font-weight: bold; color: #8B0000; text-align: left; }

/* ── SOUTH INDIAN CHART ── */
.charts-row { display: flex; justify-content: space-around; margin-bottom: 10px; page-break-inside: avoid; }
.si-chart { border-collapse: collapse; width: 260px; }
.si-chart caption { 
  font-weight: bold; font-size: 10pt; color: #8B0000; 
  padding: 4px; caption-side: top;
}
.chart-cell { 
  width: 65px; height: 50px; border: 1.5px solid #8B0000;
  font-size: 8pt; line-height: 1.1; font-weight: bold; color: #333;
  text-align: center; vertical-align: middle; padding: 1px;
}
.chart-center { 
  text-align: center; vertical-align: middle;
  font-size: 11pt; font-weight: bold; color: #8B0000;
  border: 1.5px solid #8B0000;
}

/* ── DASA TABLE ── */
.dasa-table { width: 100%; border-collapse: collapse; font-size: 9pt; margin-bottom: 10px; page-break-inside: avoid; }
.dasa-table th { background: #8B0000; color: white; padding: 4px; }
.dasa-table td { padding: 2.5px; border-bottom: 1px solid #E8D5C4; text-align: center; }
.dasa-table .dasa-lord { font-weight: bold; color: #8B0000; text-align: left; }
.dasa-remaining { 
  background: #FFD700; color: #8B0000; font-weight: bold;
  padding: 8px 15px; border-radius: 4px; margin-top: auto; margin-bottom: 10px;
  font-size: 11pt; text-align: center;
}

/* ── ASHTAKAVARGA & VARGAS ── */
.av-table { width: 100%; border-collapse: collapse; font-size: 9pt; margin-bottom: 10px; }
.av-table th { background: #8B0000; color: white; padding: 4px; font-size: 9pt; }
.av-table td { padding: 3px 5px; border: 1px solid #E8D5C4; text-align: center; }
.av-table .planet-col { font-weight: bold; color: #8B0000; text-align: left; }

.vargas-text-table { width: 100%; border-collapse: collapse; font-size: 9pt; margin-bottom: 10px; }
.vargas-text-table th { background: #8B0000; color: white; padding: 4px; font-size: 9pt; }
.vargas-text-table td { padding: 3px; border-bottom: 1px solid #E8D5C4; text-align: center; }
.vargas-text-table .planet-col { font-weight: bold; color: #8B0000; text-align: left; }

/* ── RETRO BADGE ── */
.retro-badge { 
  background: #FF6B35; color: white; font-size: 9.5pt; 
  padding: 5px 12px; border-radius: 12px; display: inline-block; text-align: center;
}

/* ── MATCHING SPECIFIC ── */
.match-header { text-align: center; margin: 10px 0; }
.match-header h2 { color: #8B0000; font-size: 14pt; }
.persons-row { display: flex; gap: 15px; margin: 8px 0; }
.person-card { 
  flex: 1; background: #FFF8F0; border: 1px solid #D4A574;
  border-radius: 6px; padding: 10px;
}
.person-card h3 { color: #8B0000; font-size: 10pt; margin-bottom: 5px; border-bottom: 1px solid #D4A574; padding-bottom: 3px; }
.person-card p { font-size: 9pt; margin: 2px 0; }

.porutham-table { width: 100%; border-collapse: collapse; margin: 6px 0; }
.porutham-table th { background: #8B0000; color: white; padding: 4px 8px; font-size: 9pt; }
.porutham-table td { padding: 3px 8px; font-size: 9pt; border-bottom: 1px solid #E8D5C4; }
.porutham-table .good { color: #228B22; font-weight: bold; }
.porutham-table .fair { color: #DAA520; font-weight: bold; }
.porutham-table .poor { color: #DC143C; font-weight: bold; }
.score-box { 
  background: linear-gradient(135deg, #8B0000, #A0522D); color: #FFD700;
  font-size: 18pt; font-weight: bold; text-align: center;
  padding: 10px; border-radius: 8px; margin: 8px 0;
}

/* ── PAGE BREAK & HEADER ── */
.page-header {
  display: flex; align-items: center; justify-content: space-between;
  border-bottom: 2px solid #8B0000; padding-bottom: 5px; margin-bottom: 20px;
  position: relative;
}
.page-header::before {
  content: ''; position: absolute; top: 50%; left: 0; right: 0; height: 1px; background: #8B0000; z-index: 1;
}
.page-header .ph-left, .page-header .ph-right { background: #fff; z-index: 2; padding: 0 10px; font-weight: bold; color: #333; }
.page-header .ph-center {
  background: #fff; z-index: 2; padding: 0 15px;
  border-radius: 50%; border: 1px solid #8B0000;
  width: 40px; height: 40px; display: flex; align-items: center; justify-content: center;
}
.page-header .ph-center img { max-width: 25px; max-height: 25px; }

/* ── FOOTER ── */
.footer { 
  text-align: center; font-size: 7.5pt; color: #777; 
  border-top: 1px solid #ddd; padding-top: 4px; margin-top: 10px;
}
`;

// ─── LETTERHEAD HTML ─────────────────────────────────────────────────────────
function letterheadHTML(lang: HoroscopeLang, pageNum: number, logoBase64?: string): string {
  const isTamil = lang === 'ta';
  const logoSrc = logoBase64 
    ? `data:image/jpeg;base64,${logoBase64}` 
    : '/images/paranthaman.png';
  
  return `
  <div class="letterhead">
    <img src="${logoSrc}" alt="Sri Paranthaman" />
    <div class="letterhead-text">
      <h1>${isTamil ? 'ஸ்ரீ பரந்தாமன் ஜோதிட சேவை' : 'SRI PARANTHAMAN ASTRO SERVICE'}</h1>
      <h2>${isTamil ? 'வாக்கிய ஜாதக கணிப்பு' : 'Vakya Horoscope Casting'}</h2>
      <p>www.ahobilam.com/panchangam | ${isTamil ? 'தொலை: 8610736302' : 'Ph: 8610736302'}</p>
    </div>
    <div class="letterhead-right">${isTamil ? 'பக்கம்' : 'Page'} ${pageNum}</div>
  </div>`;
}

// ─── SECONDARY PAGE HEADER ───────────────────────────────────────────────────
function pageHeaderHTML(name: string, pageNum: number, logoBase64?: string): string {
  const logoSrc = logoBase64 
    ? `data:image/jpeg;base64,${logoBase64}` 
    : '/images/paranthaman.png';
  
  return `
  <div class="page-header">
    <div class="ph-left">${name}</div>
    <div class="ph-center"><img src="${logoSrc}" alt="Logo" /></div>
    <div class="ph-right">${pageNum}</div>
  </div>`;
}

const TAMIL_YEAR_KEYS = [
  'PRABHAVA', 'VIBHAVA', 'SUKLA', 'PRAMODOOTA', 'PRAJOTHPATTI', 'AANGIRASA', 'SRIMUKHA',
  'BHAVA', 'YUVA', 'DHAATU', 'ESWARA', 'BAHUDANYA', 'PRAMATHI', 'VIKRAMA', 'VISHU',
  'CHITRABAANU', 'SUBHAANU', 'TAARANA', 'PAARTHIBA', 'VYAYA', 'SARVAJIT', 'SARVADHARI',
  'VIRODHI', 'VIKRUTHI', 'KARA', 'NANDANA', 'VIJAYA', 'JAYA', 'MANMATHA', 'DUNMUKHI',
  'HEVILAMBI', 'VILAMBI', 'VIKRATHI', 'SARVARI', 'PLAVA', 'SUBAKRIT', 'SOBAKRIT',
  'KRODHI', 'VISWAVASU', 'PARABHAVA', 'PLAVANGA', 'KEELAKA', 'SOWMIYA', 'SADHARANA',
  'VIRODHIKRIT', 'PARIDHABI', 'PRAMADICHA', 'ANANDA', 'RAKSHASA', 'NALA', 'PINGALA',
  'KALAYUKTHI', 'SIDDHARTHI', 'RAUDRI', 'DUNMATHI', 'DUNDUBHI', 'RUDHRODGARI',
  'RAKTAKSHI', 'KRODHANA', 'AKSHAYA'
];

const TAMIL_MONTH_KEYS = [
  'CHITHIRAI', 'VAIKASI', 'AANI', 'AADI', 'AAVANI', 'PURATTASI',
  'AIPPASI', 'KARTHIGAI', 'MARGAZHI', 'THAI', 'MASI', 'PANGUNI'
];

// ─── CASTING PAGE HTML ─────────────────────────────────────────────────────
export function generateCastingHTML(result: any, lang: HoroscopeLang = 'ta', logoBase64?: string, donationBase64?: string): string {
  const L = getLangDict(lang);
  const isTa = lang === 'ta';
  const pd = result.planetData;
  const donationImgSrc = donationBase64 ? `data:image/jpeg;base64,${donationBase64}` : '/images/Donation-request.jpg';

  const bi = result.basicInfo;
  const birthNakName = L.NAKSHATRAS[result.birthNak?.index] || result.birthNak?.name || '';
  const moonRasi = L.RASIS[pd.CHAN?.rasi] || pd.CHAN?.rasiName || '';
  const lagnaRasi = L.RASIS[pd.LAGN?.rasi] || pd.LAGN?.rasiName || '';
  const thithiName = L.THITHIS[result.thithi?.index] || result.thithi?.name || '';
  const pakshaName = L.PAKSHA[result.thithi?.paksha] || result.thithi?.paksha || '';
  const yogaName = L.YOGAS[result.yoga?.index] || result.yoga?.name || '';

  let tYearIdx = result.tamilYearIdx !== undefined 
    ? result.tamilYearIdx 
    : (result.tamilYear ? TAMIL_YEAR_KEYS.indexOf(String(result.tamilYear).toUpperCase()) : -1);
  if (tYearIdx < 0 && bi?.date) {
    const dParts = String(bi.date).replace(/\//g, '-').split('-');
    const yVal = parseInt(dParts[0].length === 4 ? dParts[0] : dParts[2] || '0', 10);
    const mVal = parseInt(dParts[0].length === 4 ? dParts[1] : dParts[1] || '0', 10);
    const dayVal = parseInt(dParts[0].length === 4 ? dParts[2] : dParts[0] || '0', 10);
    if (yVal > 0) {
      const isBeforeChithirai = pd?.SURY?.rasi === 11 || (pd?.SURY?.rasi === undefined && (mVal < 4 || (mVal === 4 && dayVal < 14)));
      const effYear = isBeforeChithirai ? yVal - 1 : yVal;
      tYearIdx = (effYear - 1987 + 60000) % 60;
    }
  }
  const tYearName = tYearIdx >= 0 ? (L.TAMIL_YEARS[tYearIdx] || result.tamilYear) : (result.tamilYear || '');
  
  let tMonthIdx = result.tamilMonthIdx !== undefined 
    ? result.tamilMonthIdx 
    : (result.tamilMonth ? TAMIL_MONTH_KEYS.indexOf(String(result.tamilMonth).toUpperCase()) : -1);
  if (tMonthIdx < 0 && pd?.SURY?.rasi !== undefined) {
    tMonthIdx = pd.SURY.rasi;
  }
  const tMonthName = tMonthIdx >= 0 ? (L.TAMIL_MONTHS[tMonthIdx] || result.tamilMonth) : (result.tamilMonth || '');
  
  const tDayStr = result.tamilDay || (pd?.SURY ? Math.floor((pd.SURY.longitude % 30) + 1) : '');

  const tamilDateFormatted = (tYearName || tMonthName) 
    ? `${tYearName ? `${tYearName} ${isTa ? 'வருடம்' : 'Year'}, ` : ''}${tMonthName ? `${tMonthName} ${isTa ? 'மாதம்' : 'Month'} ` : ''}${tDayStr ? `${tDayStr} ${isTa ? 'தேதி' : 'Date'}` : ''}`
    : '';

  const chartPlanets: Record<number, string[]> = {};
  const navamsaPlanets: Record<number, string[]> = {};
  const bhavaChartPlanets: Record<number, string[]> = {};
  for (let r = 0; r < 12; r++) { 
    chartPlanets[r] = []; 
    navamsaPlanets[r] = []; 
    bhavaChartPlanets[r] = []; 
  }

  const mainPlanets = ['SURY', 'CHAN', 'KUJA', 'BUDH', 'GURU', 'SUKR', 'SANI', 'RAHU', 'KETU', 'LAGN', 'GULI'];
  mainPlanets.forEach(p => {
    if (pd[p]) {
      const ri = pd[p].rasi;
      if (ri !== undefined && ri >= 0 && ri < 12) {
        chartPlanets[ri].push(L.PLANETS[p] || p.substring(0, 3));
      }
      const ni = pd[p].navamsa;
      if (ni !== undefined && ni >= 0 && ni < 12) {
        navamsaPlanets[ni].push(L.PLANETS[p] || p.substring(0, 3));
      }
    }
  });

  if (result.bhavaPlanets) {
    for (let r = 0; r < 12; r++) {
      if (result.bhavaPlanets[r]) {
        bhavaChartPlanets[r] = result.bhavaPlanets[r].map((p: string) => L.PLANETS[p] || p.substring(0, 3));
      }
    }
  }

  const allPlanets = ['SURY', 'CHAN', 'KUJA', 'BUDH', 'GURU', 'SUKR', 'SANI', 'RAHU', 'KETU', 'LAGN', 'GULI', 'URAN', 'NEPT', 'PLUT'];
  let planetRows = '';
  allPlanets.forEach(p => {
    if (!pd[p]) return;
    const d = pd[p];
    const nakName = L.NAKSHATRAS[d.nakIndex] || d.nakshatra || '';
    const plName = L.PLANETS_FULL[p] || p;
    const rulerName = L.PLANETS_FULL[d.ruler] || d.ruler || '';
    const rasiName = L.RASIS[d.rasi] || d.rasiName || '';
    const retro = d.retrograde ? ' ®' : '';
    planetRows += `<tr>
      <td class="planet-name">${plName}${retro}</td>
      <td>${d.degStr || ''}</td>
      <td>${rasiName}</td>
      <td>${nakName}</td>
      <td>${d.pada || ''}</td>
      <td>${rulerName}</td>
    </tr>`;
  });

  const retroPlanets = allPlanets.filter(p => pd[p]?.retrograde).map(p => L.PLANETS_FULL[p] || p);

  const dasa = result.dasaInfo;
  let dasaRows = '';
  if (dasa?.sequence) {
    dasa.sequence.forEach(d => {
      const lordName = L.PLANETS_FULL[d.lord] || d.lord;
      const endDate = d.endDate ? `${String(d.endDate.getDate()).padStart(2,'0')}-${String(d.endDate.getMonth() + 1).padStart(2,'0')}-${d.endDate.getFullYear()}` : '';
      let bukCells = '';
      (d.bukthis || []).forEach(b => {
        const bName = (L.PLANETS_FULL[b.lord] || b.lord).substring(0, 4);
        const bd = b.endDate;
        const bDate = bd ? `${String(bd.getDate()).padStart(2,'0')}-${String(bd.getMonth() + 1).padStart(2,'0')}-${String(bd.getFullYear()).slice(-2)}` : '';
        bukCells += `<td>${bName}<br/><span style="font-size:7pt;color:#666">${bDate}</span></td>`;
      });
      dasaRows += `<tr><td class="dasa-lord">${lordName}</td><td>${endDate}</td>${bukCells}</tr>`;
    });
  }

  let bhavaRows = '';
  if (result.bhavas) {
    result.bhavas.forEach((b: any, i: number) => {
      bhavaRows += `<tr><td>${i + 1}</td><td>${b.midDeg || ''}</td><td>${b.startDeg || ''}</td><td>${L.PLANETS_FULL[b.lord] || b.lord || ''}</td></tr>`;
    });
  }

  return `<!DOCTYPE html><html><head>
<meta charset="UTF-8">
<style>${PDF_CSS}</style>
</head><body>

<div class="page">
  ${letterheadHTML(lang, 1, logoBase64)}
  <div class="page-content">
    <div class="birth-info">
      <table>
        <tr>
          <td class="label">${isTa ? 'பெயர்' : 'Name'}</td><td class="value"><b>${bi.name}</b></td>
          <td colspan="2"></td>
        </tr>
        <tr>
          <td class="label">${isTa ? 'பிறந்த தேதி' : 'Date of Birth'}</td><td class="value">${bi.date}</td>
          <td class="label">${isTa ? 'நேரம்' : 'Time'}</td><td class="value">${bi.time} IST</td>
        </tr>
        <tr>
          <td class="label">${isTa ? 'பிறந்த இடம்' : 'Place of Birth'}</td><td class="value">${bi.place}</td>
          <td class="label">${isTa ? 'நேர வலயம்' : 'Time Zone'}</td><td class="value">UTC +5.5</td>
        </tr>
        <tr>
          <td class="label">${isTa ? 'அட்சரேகை' : 'Latitude'}</td><td class="value">${bi.latitude}°</td>
          <td class="label">${isTa ? 'தீர்க்கரேகை' : 'Longitude'}</td><td class="value">${bi.longitude}°</td>
        </tr>
      </table>
    </div>

    <div class="panchanga-line">
      ${tamilDateFormatted ? `<span class="tamil-date-badge">${tamilDateFormatted}</span> &nbsp;|&nbsp; ` : ''}${birthNakName} ${isTa ? 'நக்ஷத்ரம்' : 'Star'}, ${isTa ? 'பாதம்' : 'Pada'} ${result.birthNak?.pada || ''} &nbsp;|&nbsp; ${moonRasi} ${isTa ? 'ராசி' : 'Rasi'} &nbsp;|&nbsp; ${lagnaRasi} ${isTa ? 'லக்னம்' : 'Lagna'} &nbsp;|&nbsp; ${pakshaName} ${thithiName} ${isTa ? 'திதி' : 'Thithi'} &nbsp;|&nbsp; ${yogaName} ${isTa ? 'யோகம்' : 'Yoga'}
    </div>

    <div>
      <div class="section-title">${L.NIRAYANA}</div>
      <table class="planet-table">
        <tr><th>${isTa ? 'கிரகம்' : 'Planet'}</th><th>${isTa ? 'பாகை' : 'Deg:Mn'}</th><th>${isTa ? 'ராசி' : 'Rasi'}</th><th>${isTa ? 'நக்ஷத்ரம்' : 'Star'}</th><th>${isTa ? 'பாதம்' : 'Pada'}</th><th>${isTa ? 'அதிபதி' : 'Ruler'}</th></tr>
        ${planetRows}
      </table>
    </div>

    ${retroPlanets.length > 0 ? `<div style="text-align: center;"><div class="retro-badge">${isTa ? 'வக்ர கிரகங்கள்' : 'Retrograde'}: ${retroPlanets.join(', ')}</div></div>` : '<div></div>'}

    <div class="charts-row">
      ${renderChartHTML(chartPlanets, isTa ? 'ராசி' : 'RASI', lang)}
      ${renderChartHTML(navamsaPlanets, isTa ? 'நவாம்சம்' : 'NAVAMSA', lang)}
    </div>

    <div class="dasa-remaining">
      ${L.PLANETS_FULL[dasa?.birthLord] || dasa?.birthLord || ''} ${isTa ? 'தசை மீதம்' : 'Dasa Balance'}: 
      ${Math.floor(dasa?.remainYears || 0)} ${isTa ? 'வரு.' : 'Yrs'} 
      ${dasa?.remainMonths || 0} ${isTa ? 'மா.' : 'Mon'} 
      ${dasa?.remainDays || 0} ${isTa ? 'நா.' : 'Days'}
    </div>
  </div>
</div>

<div class="page">
  ${pageHeaderHTML(bi.name, 2, logoBase64)}
  <div class="page-content">
    <div>
      <div class="section-title">${isTa ? 'தசா / புக்தி காலம்' : 'DASA / BUKTHI PERIODS'}</div>
      <table class="dasa-table">
        <tr><th>${isTa ? 'தசை' : 'Dasa'}</th><th>${isTa ? 'முடிவு' : 'Ends'}</th>
        <th colspan="9">${isTa ? 'புக்தி காலங்கள்' : 'Bukthi Periods'}</th></tr>
        ${dasaRows}
      </table>
    </div>

    <div>
      <div class="section-title">${isTa ? 'பாவ அட்டவணை' : 'BHAVA TABLE'}</div>
      <table class="planet-table">
        <tr><th>${isTa ? 'பாவம்' : 'Bhava'}</th><th>${isTa ? 'மத்தியம்' : 'Middle'}</th><th>${isTa ? 'ஆரம்பம்' : 'Start'}</th><th>${isTa ? 'அதிபதி' : 'Lord'}</th></tr>
        ${bhavaRows}
      </table>
    </div>

    ${result.bhavaPlanets ? `
    <div class="charts-row" style="justify-content: center;">
      ${renderChartHTML(bhavaChartPlanets, isTa ? 'பாவம்' : 'BHAVA', lang)}
    </div>
    ` : ''}
  </div>
</div>

${result.vargasTextTable ? `
<div class="page">
  ${pageHeaderHTML(bi.name, 3, logoBase64)}
  <div class="page-content">
    <div>
      <div class="section-title">${isTa ? 'வர்க்கங்கள் (Vargas)' : 'VARGAS'}</div>
      <table class="vargas-text-table">
        <tr><th>${isTa ? 'கிரகம்' : 'PLANET'}</th><th>${isTa ? 'ராசி' : 'RASI'}</th><th>${isTa ? 'ஹோரை' : 'HORA'}</th><th>${isTa ? 'திரேக்காணம்' : 'DREKAN'}</th><th>${isTa ? 'சப்தாம்சம்' : 'SAPTMSA'}</th><th>${isTa ? 'நவாம்சம்' : 'NAVAMSA'}</th><th>${isTa ? 'துவாதசாம்சம்' : 'DWADASA'}</th><th>${isTa ? 'த்ரிம்சாம்சம்' : 'THRIMSA'}</th><th>${isTa ? 'தசாம்சம்' : 'DASAMSA'}</th></tr>
        ${['SURY','CHAN','KUJA','BUDH','GURU','SUKR','SANI','RAHU','KETU','LAGN'].map(p => {
          const v = result.vargasTextTable[p];
          if (!v) return '';
          return `<tr>
            <td class="planet-col">${L.PLANETS[p] || p}</td>
            <td>${L.RASIS[v.rasi] || v.rasi}</td>
            <td>${L.PLANETS[v.hora] || v.hora}</td>
            <td>${L.RASIS[v.drekkana] || v.drekkana}</td>
            <td>${L.RASIS[v.sapthamsa] || v.sapthamsa}</td>
            <td>${L.RASIS[v.navamsa] || v.navamsa}</td>
            <td>${L.RASIS[v.dwadasamsa] || v.dwadasamsa}</td>
            <td>${L.PLANETS[v.trimsamsa] || v.trimsamsa}</td>
            <td>${L.RASIS[v.dasamsa] || v.dasamsa}</td>
          </tr>`;
        }).join('')}
      </table>
    </div>
    
    ${result.vargas ? `
    <div class="charts-row">
      ${renderChartHTML(result.vargas.D3, isTa ? 'திரேக்காணம் (D3)' : 'DREKKANA', lang)}
      ${renderChartHTML(result.vargas.D7, isTa ? 'சப்தாம்சம் (D7)' : 'SAPTHAMSA', lang)}
    </div>
    <div class="charts-row">
      ${renderChartHTML(result.vargas.D12, isTa ? 'துவாதசாம்சம் (D12)' : 'DWADASAMSA', lang)}
      ${renderChartHTML(result.vargas.D30, isTa ? 'த்ரிம்சாம்சம் (D30)' : 'TRIMSAMSA', lang)}
    </div>
    ` : ''}

    ${result.ashtaka ? `
    <div>
      <div class="section-title">${isTa ? 'அஷ்டகவர்க்கம் (Ashtakavarga)' : 'ASHTAKAVARGA'}</div>
      <table class="av-table">
        <tr>
          <th>Planet</th>
          ${Array.from({length: 12}, (_, i) => `<th>${L.RASIS_SHORT[i] || i+1}</th>`).join('')}
          <th>Total</th>
        </tr>
        ${['SURY', 'CHAN', 'KUJA', 'BUDH', 'GURU', 'SUKR', 'SANI', 'LAGN'].map(p => {
          if (!result.ashtaka.raw[p]) return '';
          const row = result.ashtaka.raw[p];
          const sum = row.reduce((a,b)=>a+b, 0);
          return `<tr>
            <td class="planet-col">${L.PLANETS[p] || p}</td>
            ${row.map(v => `<td>${v}</td>`).join('')}
            <td><b>${sum}</b></td>
          </tr>`;
        }).join('')}
        <tr>
          <td class="planet-col"><b>${isTa ? 'மொத்தம்' : 'TOTAL'}</b></td>
          ${Array.from({length: 12}, (_, i) => {
             const colSum = ['SURY', 'CHAN', 'KUJA', 'BUDH', 'GURU', 'SUKR', 'SANI', 'LAGN']
               .reduce((acc, p) => acc + (result.ashtaka.raw[p] ? result.ashtaka.raw[p][i] : 0), 0);
             return `<td><b>${colSum}</b></td>`;
          }).join('')}
          <td><b>${
             ['SURY', 'CHAN', 'KUJA', 'BUDH', 'GURU', 'SUKR', 'SANI', 'LAGN']
               .reduce((acc, p) => acc + (result.ashtaka.raw[p] ? result.ashtaka.raw[p].reduce((a,b)=>a+b,0) : 0), 0)
          }</b></td>
        </tr>
      </table>
    </div>
    ` : ''}

    <div class="donation-card" style="text-align: center; margin: 8px 0 4px 0;">
      <img src="${donationImgSrc}" style="width: 100%; max-height: 125px; object-fit: contain; border-radius: 6px; border: 1px solid #D4A574;" alt="Donation Request" />
    </div>

    <div class="footer" style="text-align: center; font-size: 8.5pt; color: #777; margin-top: auto; padding-top: 6px;">
      ${isTa ? 'ஸ்ரீ பரந்தாமன் ஜோதிட சேவை — www.ahobilam.com/panchangam — வாக்கிய கணிதம்' : 'Sri Paranthaman Astro Service — www.ahobilam.com/panchangam — Vakya Calculation'}
    </div>
  </div>
</div>
` : ''}

</body></html>`;
}

// ─── MATCHING TEMPLATE ───────────────────────────────────────────────────────
export function generateMatchingHTML(matchResult: any, lang: HoroscopeLang = 'ta', logoBase64?: string, donationBase64?: string): string {
  const L = getLangDict(lang);
  const isTa = lang === 'ta';
  const donationImgSrc = donationBase64 ? `data:image/jpeg;base64,${donationBase64}` : '/images/Donation-request.jpg';
  
  // Extract data from matchResult
  const boyInfo = matchResult.boyInfo || {};
  const girlInfo = matchResult.girlInfo || {};
  const poruthams = matchResult.poruthams || [];
  const totalScore = matchResult.totalScore || 0;

  // Chart data
  const boyRasiPlanets = matchResult.boyRasiPlanets || {};
  const boyNavamsaPlanets = matchResult.boyNavamsaPlanets || {};
  const girlRasiPlanets = matchResult.girlRasiPlanets || {};
  const girlNavamsaPlanets = matchResult.girlNavamsaPlanets || {};

  // Dosha & Dasa
  const boyDosha = matchResult.boyDosha || [];
  const girlDosha = matchResult.girlDosha || [];
  const hasDasaSandhi = matchResult.hasDasaSandhi;
  const resultSummary = matchResult.resultSummary;
  const rajjuDosha = matchResult.rajjuDosha;
  const nadiDosha = matchResult.nadiDosha;

  // Build Porutham rows
  let poruthamRows = '';
  poruthams.forEach((p: any, i: number) => {
    const name = L.PORUTHAM_NAMES?.[i] || p.name || '';
    const matchClass = p.result === 'GOOD' ? 'good' : p.result === 'FAIR' ? 'fair' : 'poor';
    const matchText = p.result === 'GOOD' ? L.GOOD : p.result === 'FAIR' ? L.FAIR : L.POOR;
    poruthamRows += `<tr>
      <td>${i + 1}</td><td>${name}</td>
      <td>${p.boyValue || ''}</td><td>${p.girlValue || ''}</td>
      <td class="${matchClass}">${matchText}</td>
      <td>${p.points || 0}</td>
    </tr>`;
  });

  // Build Rasi & Navamsa chart HTML
  const boyRasiChart = Object.keys(boyRasiPlanets).length > 0
    ? renderChartHTML(boyRasiPlanets, isTa ? 'ராசி — ஆண்' : 'RASI — BOY', lang) : '';
  const girlRasiChart = Object.keys(girlRasiPlanets).length > 0
    ? renderChartHTML(girlRasiPlanets, isTa ? 'ராசி — பெண்' : 'RASI — GIRL', lang) : '';
  const boyNavamsaChart = Object.keys(boyNavamsaPlanets).length > 0
    ? renderChartHTML(boyNavamsaPlanets, isTa ? 'நவாம்சம் — ஆண்' : 'AMSA — BOY', lang) : '';
  const girlNavamsaChart = Object.keys(girlNavamsaPlanets).length > 0
    ? renderChartHTML(girlNavamsaPlanets, isTa ? 'நவாம்சம் — பெண்' : 'AMSA — GIRL', lang) : '';

  // Build Dosha Samya rows
  let doshaTableHTML = '';
  if (boyDosha.length > 0 && girlDosha.length > 0) {
    let boyTotM = 0, boyTotS = 0, boyTotL = 0, boyTotT = 0;
    let girlTotM = 0, girlTotS = 0, girlTotL = 0, girlTotT = 0;
    let doshaRows = '';
    const planetNames = ['SURY', 'KUJA', 'SANI', 'RAHU', 'KETU'];
    for (let i = 0; i < boyDosha.length; i++) {
      const b = boyDosha[i], g = girlDosha[i];
      const bm = parseFloat(b.moon) || 0, bs = parseFloat(b.sukr) || 0, bl = parseFloat(b.lagn) || 0;
      const gm = parseFloat(g.moon) || 0, gs = parseFloat(g.sukr) || 0, gl = parseFloat(g.lagn) || 0;
      const bt = bm + bs + bl, gt = gm + gs + gl;
      boyTotM += bm; boyTotS += bs; boyTotL += bl; boyTotT += bt;
      girlTotM += gm; girlTotS += gs; girlTotL += gl; girlTotT += gt;
      doshaRows += `<tr>
        <td class="planet-col">${planetNames[i] || ''}</td>
        <td>${(b.state || '').trim()}</td><td>${bm.toFixed(2)}</td><td>${bs.toFixed(2)}</td><td>${bl.toFixed(2)}</td><td><b>${bt.toFixed(2)}</b></td>
        <td>${(g.state || '').trim()}</td><td>${gm.toFixed(2)}</td><td>${gs.toFixed(2)}</td><td>${gl.toFixed(2)}</td><td><b>${gt.toFixed(2)}</b></td>
      </tr>`;
    }
    doshaRows += `<tr style="background:#FFF0E0;font-weight:bold;">
      <td>${isTa ? 'மொத்தம்' : 'TOTAL'}</td>
      <td></td><td>${boyTotM.toFixed(2)}</td><td>${boyTotS.toFixed(2)}</td><td>${boyTotL.toFixed(2)}</td><td>${boyTotT.toFixed(2)}</td>
      <td></td><td>${girlTotM.toFixed(2)}</td><td>${girlTotS.toFixed(2)}</td><td>${girlTotL.toFixed(2)}</td><td>${girlTotT.toFixed(2)}</td>
    </tr>`;

    doshaTableHTML = `
    <div class="section-title">${isTa ? 'தோஷ சாம்யம் (குரு பார்வை இருப்பின் தோஷமில்லை)' : 'DOSHA SAMYA (No Dosha if Guru Aspects)'}</div>
    <table class="planet-table">
      <tr>
        <th rowspan="2">${isTa ? 'கிரகம்' : 'Planet'}</th>
        <th colspan="5" style="background:#1565c0;">── ${isTa ? 'ஆண்' : 'BOY'} ──</th>
        <th colspan="5" style="background:#c62828;">── ${isTa ? 'பெண்' : 'GIRL'} ──</th>
      </tr>
      <tr>
        <th>${isTa ? 'நிலை' : 'State'}</th><th>${isTa ? 'சந்' : 'Moon'}</th><th>${isTa ? 'சுக்' : 'Sukr'}</th><th>${isTa ? 'லக்' : 'Lagn'}</th><th>${isTa ? 'மொத்' : 'Total'}</th>
        <th>${isTa ? 'நிலை' : 'State'}</th><th>${isTa ? 'சந்' : 'Moon'}</th><th>${isTa ? 'சுக்' : 'Sukr'}</th><th>${isTa ? 'லக்' : 'Lagn'}</th><th>${isTa ? 'மொத்' : 'Total'}</th>
      </tr>
      ${doshaRows}
    </table>`;
  }

  // Dasa Sandhi
  let dasaSandhiHTML = '';
  if (hasDasaSandhi !== undefined) {
    const dsText = hasDasaSandhi
      ? (isTa ? 'தசா சந்தி உள்ளது, ஆனால் புக்தி சந்தி இல்லை; சரி' : 'DASA SANDHI OCCURS, BUT NO BUKTHI SANDHI; OK')
      : (isTa ? 'தசா சந்தி இல்லை. ஏற்றுக்கொள்ளத்தக்கது' : 'DASA SANDHI DOES NOT OCCUR. ACCEPTABLE');
    const dsColor = hasDasaSandhi ? '#e65100' : '#228B22';
    dasaSandhiHTML = `<div style="background:${dsColor}15;border:1px solid ${dsColor};border-radius:6px;padding:8px 12px;margin:8px 0;font-weight:bold;color:${dsColor};text-align:center;font-size:10pt;">
      ${dsText}
    </div>`;
  }

  // Result summary
  let resultHTML = '';
  if (resultSummary) {
    let resText = '', resColor = '#228B22';
    if (resultSummary === 'good_all') {
      resText = isTa ? 'ஜாதக பொருத்தம் அனைத்து வகையிலும் நல்லது.' : 'The Jadhaka Porutham is found good in all respect.';
    } else if (resultSummary === 'good_general') {
      resText = isTa ? 'ஜாதக பொருத்தம் பொதுவாக நல்லது.' : 'The Jadhaka Porutham is found generally good.';
      resColor = '#1565c0';
    } else {
      resText = isTa ? 'ஜாதக பொருத்தம் சில பொருத்தமின்மைகளைக் காட்டுகிறது.' : 'The Jadhaka Porutham shows some incompatibilities.';
      resColor = '#c62828';
    }
    let doshaWarnings = '';
    if (rajjuDosha) doshaWarnings += `<div style="color:#c62828;font-size:9pt;margin-top:4px;">⚠️ ${isTa ? 'ரஜ்ஜு தோஷம் உள்ளது' : 'Rajju Dosha is present'} (${rajjuDosha})</div>`;
    if (nadiDosha) doshaWarnings += `<div style="color:#c62828;font-size:9pt;margin-top:2px;">⚠️ ${isTa ? 'நாடி தோஷம் உள்ளது' : 'Nadi Dosha is present'} (${nadiDosha})</div>`;

    resultHTML = `<div style="background:#f5f5f5;border:2px solid ${resColor};border-radius:8px;padding:10px;margin:8px 0;text-align:center;">
      <div style="font-weight:bold;font-size:11pt;color:${resColor};">${isTa ? 'முடிவு' : 'Result'}: ${resText}</div>
      ${doshaWarnings}
    </div>`;
  }

  return `<!DOCTYPE html><html><head>
<meta charset="UTF-8">
<style>${PDF_CSS}</style>
</head><body>

${letterheadHTML(lang, 1, logoBase64)}

<div class="match-header">
  <h2>${L.HOROSCOPE_MATCHING}</h2>
</div>

<div class="persons-row">
  <div class="person-card">
    <h3>👦 ${L.BOY}</h3>
    <p><b>${L.NAME}:</b> ${boyInfo.name || ''}</p>
    <p><b>${isTa ? 'பிறந்த தேதி' : 'DOB'}:</b> ${boyInfo.date || ''} ${boyInfo.time || ''}</p>
    <p><b>${L.PLACE_OF_BIRTH}:</b> ${boyInfo.place || ''}</p>
    <p><b>${L.NAKSHATRA_LABEL}:</b> ${boyInfo.nakshatra || ''} ${isTa ? 'பாதம்' : 'Pada'} ${boyInfo.pada || ''}</p>
  </div>
  <div class="person-card">
    <h3>👧 ${L.GIRL}</h3>
    <p><b>${L.NAME}:</b> ${girlInfo.name || ''}</p>
    <p><b>${isTa ? 'பிறந்த தேதி' : 'DOB'}:</b> ${girlInfo.date || ''} ${girlInfo.time || ''}</p>
    <p><b>${L.PLACE_OF_BIRTH}:</b> ${girlInfo.place || ''}</p>
    <p><b>${L.NAKSHATRA_LABEL}:</b> ${girlInfo.nakshatra || ''} ${isTa ? 'பாதம்' : 'Pada'} ${girlInfo.pada || ''}</p>
  </div>
</div>

${(boyRasiChart || girlRasiChart) ? `
<div class="section-title">${isTa ? 'ராசி கட்டம்' : 'RASI CHART'}</div>
<div class="charts-row">${boyRasiChart}${girlRasiChart}</div>
` : ''}

${(boyNavamsaChart || girlNavamsaChart) ? `
<div class="section-title">${isTa ? 'நவாம்ச கட்டம்' : 'NAVAMSA CHART'}</div>
<div class="charts-row">${boyNavamsaChart}${girlNavamsaChart}</div>
` : ''}

<div class="section-title">${L.PORUTHAMS} (${isTa ? 'குடும்பப் பொருத்தம்' : '12 Kuttas'})</div>
<table class="porutham-table">
  <tr><th>#</th><th>${isTa ? 'பொருத்தம்' : 'Porutham'}</th><th>${L.BOY}</th><th>${L.GIRL}</th><th>${L.MATCH}</th><th>${L.POINTS}</th></tr>
  ${poruthamRows}
</table>

<div class="score-box">
  ${isTa ? 'மொத்த மதிப்பெண்' : 'TOTAL SCORE'}: ${totalScore} / 12
</div>

${doshaTableHTML}

${dasaSandhiHTML}

${resultHTML}

<div class="donation-card" style="text-align: center; margin: 8px 0 4px 0;">
  <img src="${donationImgSrc}" style="width: 100%; max-height: 125px; object-fit: contain; border-radius: 6px; border: 1px solid #D4A574;" alt="Donation Request" />
</div>

<div class="footer">
  ${isTa ? 'ஸ்ரீ பரந்தாமன் ஜோதிட சேவை — www.ahobilam.com/panchangam' : 'Sri Paranthaman Astro Service — www.ahobilam.com/panchangam'}
</div>

</body></html>`;
}
